// Additional animal and fantasy forms of the original species (see FORMS in genome.ts). Each
// keeps its species' silhouette family + behaviour roles (claws snap, flippers clap, wings flap,
// tendrils sway, Wobbler bodies ripple) and expresses the genome traits through the shared
// helpers in parts.ts.
import { Container, Graphics } from "pixi.js";
import { INK, mix } from "../palette";
import { Rng } from "../lib/rng";
import type { CreatureGenome } from "./genome";
import { blob, blot, chord, disc, inkLine, noodle, oval, traceDome } from "./draw";
import {
  antennae,
  bodyOval,
  drawBackSpikes,
  drawBlush,
  drawHorns,
  drawMarkings,
  drawMohawk,
  drawTufts,
  eyeLayout,
  limb,
  newAnatomyParts,
  pal,
  sides,
  snapClaw,
  traceFur,
  type Anatomy,
  type Colors,
  type Geo,
  type Limb,
} from "./parts";

const WING_TINT = 0xd6f0ff;
const FAIRY_WING = 0x9fd3f2;

/** Translucent insect / fairy wing hanging along local +y (rest angle lifts it). */
function insectWing(
  parent: Container,
  limbs: Limb[],
  drawers: Anatomy["drawers"],
  x: number,
  y: number,
  len: number,
  wid: number,
  rest: number,
  s: -1 | 1,
  index: number,
  ow: number,
  tint = WING_TINT,
): void {
  const w = limb(parent, x, y, "wing", s, index, rest);
  limbs.push(w.l);
  drawers.push((c) => {
    const g = w.g;
    g.clear();
    g.ellipse(0, len / 2, wid, len / 2)
      .fill({ color: mix(tint, c.light, 0.2), alpha: 0.75 })
      .stroke({ width: ow * 0.8, color: INK });
    inkLine(g, [0, len * 0.08, wid * 0.2, len * 0.5, 0, len * 0.88], ow * 0.45, true);
  });
}

// ---------------------------------------------------------------------------------------------
// Thumper / frog: squat wide body, bulging eyes on top, huge mouth, folded legs.

export function frog(gn: CreatureGenome, H: number, ow: number): Anatomy {
  const ry = H * 0.33 * gn.heightScale,
    rx = ry * gn.bodyAspect * 1.22,
    cy = -ry - H * 0.04;
  const e = eyeLayout(gn, H, rx, cy - ry * 0.82, 1.2);
  const mouthY = cy + ry * 0.12;
  const geo: Geo = {
    cy,
    rx,
    ry,
    centerY: cy,
    halfW: rx + H * 0.08,
    headTopY: cy - ry,
    headW: rx * 0.9,
    topY: e.eyeY - e.eyeR * 1.5,
    ...e,
    mouthY,
    mouthW: Math.min(gn.mouthWidth * 2.2 * rx, rx * 1.35),
    neckY: mouthY + H * 0.12,
    neckHalfW: chord(rx, ry, mouthY + H * 0.12 - cy) * 1.02,
    floats: false,
  };
  const { back, front, body, limbs, drawers } = newAnatomyParts();
  for (const s of sides) {
    // Folded back leg: big thigh + flat webbed foot; jumps kick it outward.
    const leg = limb(back, s * rx * 0.62, cy + ry * 0.45, "leg", s, 0, 0);
    limbs.push(leg.l);
    const fy = -(cy + ry * 0.45);
    drawers.push((c) => {
      const g = leg.g;
      g.clear();
      oval(g, s * H * 0.1, fy * 0.35, H * 0.16 * gn.limbLength, H * 0.12, c.base, ow);
      blob(
        g,
        [
          s * H * 0.08,
          fy - H * 0.02,
          s * H * 0.3 * gn.span,
          fy - H * 0.04,
          s * H * 0.34 * gn.span,
          fy,
          s * H * 0.06,
          fy,
        ],
        c.accent,
        ow,
        true,
      );
    });
    const arm = limb(front, s * rx * 0.38, cy + ry * 0.62, "arm", s, 0, 0);
    limbs.push(arm.l);
    drawers.push((c) => {
      arm.g.clear();
      oval(arm.g, 0, H * 0.06, H * 0.05, H * 0.08, c.base, ow);
      for (const k of [-1, 0, 1]) disc(arm.g, k * H * 0.035, H * 0.13, H * 0.022, c.accent, ow * 0.6);
    });
  }
  const drawBody = (c: Colors) => {
    const g = body;
    g.clear();
    drawMohawk(g, gn, 0, cy - ry * 0.9, H, c, ow);
    drawBackSpikes(g, gn, 0, cy, rx, ry, H, c, ow, -0.75, -0.25);
    // Eye bumps behind the eyes.
    for (const s of sides) disc(g, s * e.eyeDx, e.eyeY + e.eyeR * 0.15, e.eyeR * 1.32, c.base, ow);
    bodyOval(g, gn, 0, cy, rx, ry, c.base, ow);
    for (const s of sides) blot(g, s * e.eyeDx, e.eyeY + e.eyeR * 0.3, e.eyeR * 1.2, e.eyeR * 1.0, c.base);
    blot(g, 0, cy + ry * 0.5, rx * 0.7, ry * 0.42, c.belly);
    drawMarkings(g, gn, cy, rx, ry, H, c);
    drawBlush(g, gn, mouthY - e.eyeR * 1.6, rx * 0.55, e.eyeR);
  };
  return { geo, back, body, front, limbs, drawers, drawBody };
}

// ---------------------------------------------------------------------------------------------
// Thumper / golem (fantasy): faceted rock body, moss, glowing crystals, boulder fists.

export function golem(gn: CreatureGenome, H: number, ow: number): Anatomy {
  const legH = H * 0.08 * gn.limbLength,
    ry = H * 0.4 * gn.heightScale,
    rx = ry * gn.bodyAspect * 0.95,
    cy = -legH - ry;
  const e = eyeLayout(gn, H, rx, cy - ry * 0.25, 0.8);
  const rng = new Rng(gn.seed ^ 0x601e);
  const N = 9;
  const rock: number[] = [];
  for (let i = 0; i < N; i++) {
    const a = (i / N) * Math.PI * 2 - Math.PI / 2 + rng.range(-0.12, 0.12);
    const k = rng.range(0.92, 1.06);
    rock.push(Math.cos(a) * rx * k, Math.min(cy + Math.sin(a) * ry * k, -legH * 0.5));
  }
  const crystals: { x: number; y: number; a: number; l: number }[] = [];
  const nc = Math.max(2, Math.min(5, gn.spikes || 2));
  for (let i = 0; i < nc; i++) {
    const t = nc === 1 ? 0.5 : i / (nc - 1);
    const a = -Math.PI * (0.85 - 0.7 * t) + rng.range(-0.08, 0.08);
    crystals.push({
      x: Math.cos(a) * rx * 0.85,
      y: cy + Math.sin(a) * ry * 0.85,
      a,
      l: H * (0.12 + 0.1 * gn.traits.spikiness) * rng.range(0.8, 1.2),
    });
  }
  const mouthY = cy + ry * 0.2;
  const geo: Geo = {
    cy,
    rx,
    ry,
    centerY: cy,
    halfW: rx + H * 0.14 * gn.span,
    headTopY: cy - ry,
    headW: rx,
    topY: cy - ry - H * 0.2,
    ...e,
    mouthY,
    mouthW: gn.mouthWidth * rx * 1.3,
    neckY: mouthY + H * 0.14,
    neckHalfW: chord(rx, ry, mouthY + H * 0.14 - cy),
    floats: false,
  };
  const { back, front, body, limbs, drawers } = newAnatomyParts();
  for (const s of sides) {
    const leg = limb(back, s * rx * 0.45, -legH * 1.2, "leg", s, 0, 0);
    limbs.push(leg.l);
    drawers.push((c) => {
      leg.g.clear();
      leg.g
        .roundRect(-H * 0.09, 0, H * 0.18, legH * 1.2, H * 0.03)
        .fill({ color: mix(c.base, INK, 0.25) })
        .stroke({ width: ow, color: INK });
    });
    const arm = limb(front, s * rx * 0.92, cy - ry * 0.05, "arm", s, 0, 0.2);
    limbs.push(arm.l);
    const L = H * 0.26 * gn.span;
    drawers.push((c) => {
      const g = arm.g;
      g.clear();
      blob(g, [-H * 0.06, 0, H * 0.06, 0, H * 0.07, L * 0.7, -H * 0.07, L * 0.75], mix(c.base, INK, 0.15), ow, false);
      blob(
        g,
        [-H * 0.1, L * 0.62, H * 0.08, L * 0.6, H * 0.11, L * 0.95, 0, L * 1.08, -H * 0.11, L * 0.95],
        mix(c.base, INK, 0.08),
        ow,
        false,
      );
    });
  }
  const drawBody = (c: Colors) => {
    const g = body;
    g.clear();
    drawMohawk(g, gn, 0, cy - ry, H, c, ow);
    for (let i = 0; i < crystals.length; i++) {
      const k = crystals[i];
      const ux = Math.cos(k.a),
        uy = Math.sin(k.a),
        w = H * 0.045;
      blob(
        g,
        [
          k.x - uy * w,
          k.y + ux * w,
          k.x + ux * k.l - uy * w * 0.6,
          k.y + uy * k.l + ux * w * 0.6,
          k.x + ux * k.l * 1.25,
          k.y + uy * k.l * 1.25,
          k.x + ux * k.l + uy * w * 0.6,
          k.y + uy * k.l - ux * w * 0.6,
          k.x + uy * w,
          k.y - ux * w,
        ],
        mix(pal(c, i, 0x56b4e9), 0xffffff, 0.35),
        ow * 0.85,
        false,
      );
    }
    blob(g, rock, c.base, ow, false);
    // Facet shading + cracks.
    g.poly([rock[0], rock[1], rock[2], rock[3], 0, cy - ry * 0.2]).fill({ color: 0xffffff, alpha: 0.12 });
    inkLine(g, [-rx * 0.55, cy + ry * 0.1, -rx * 0.35, cy + ry * 0.25, -rx * 0.42, cy + ry * 0.45], ow * 0.6);
    inkLine(g, [rx * 0.5, cy - ry * 0.5, rx * 0.38, cy - ry * 0.32], ow * 0.6);
    // Moss cap.
    if (!gn.mohawk) {
      const moss = gn.markings.multi ? pal(c, 1) : 0x009e73;
      g.moveTo(-rx * 0.5, cy - ry * 0.78)
        .quadraticCurveTo(0, cy - ry * 1.12, rx * 0.5, cy - ry * 0.78)
        .quadraticCurveTo(rx * 0.3, cy - ry * 0.62, rx * 0.15, cy - ry * 0.74)
        .quadraticCurveTo(0, cy - ry * 0.6, -rx * 0.2, cy - ry * 0.74)
        .quadraticCurveTo(-rx * 0.35, cy - ry * 0.62, -rx * 0.5, cy - ry * 0.78)
        .closePath()
        .fill({ color: moss })
        .stroke({ width: ow * 0.7, color: INK, join: "round" });
    }
    blot(g, 0, cy + ry * 0.45, rx * 0.5, ry * 0.32, c.belly, 0.7);
    drawMarkings(g, gn, cy, rx * 0.85, ry * 0.85, H, c, 0.8);
    // Heavy brow.
    for (const s of sides)
      inkLine(
        g,
        [s * (e.eyeDx - e.eyeR), e.eyeY - e.eyeR * 1.35, s * (e.eyeDx + e.eyeR * 0.9), e.eyeY - e.eyeR * 1.15],
        ow * 1.3,
      );
    drawBlush(g, gn, e.eyeY, e.eyeDx, e.eyeR);
  };
  return { geo, back, body, front, limbs, drawers, drawBody };
}

// ---------------------------------------------------------------------------------------------
// Snapper / scorpion: low body, snapping pincers, segmented tail arching over the head.

export function scorpion(gn: CreatureGenome, H: number, ow: number): Anatomy {
  const ry = H * 0.2 * gn.heightScale,
    rx = ry * gn.bodyAspect * 1.25,
    cy = -H * 0.13 - ry;
  const e = eyeLayout(gn, H, rx * 0.7, cy - ry * 0.25, 0.75);
  const cs = gn.clawSize * (0.8 + 0.25 * gn.span) * 0.9;
  const tl = gn.tailLength;
  const geo: Geo = {
    cy,
    rx,
    ry,
    centerY: cy,
    halfW: rx + H * 0.22 * cs,
    headTopY: cy - ry,
    headW: rx * 0.8,
    topY: cy - ry - H * 0.55 * tl,
    ...e,
    mouthY: cy + ry * 0.45,
    mouthW: gn.mouthWidth * 2 * rx * 0.6,
    neckY: cy + ry * 0.2,
    neckHalfW: rx * 0.95,
    floats: false,
  };
  const { back, front, body, limbs, drawers } = newAnatomyParts();
  // Tail: segments rising behind the body and curling forward, stinger on top.
  const tail = limb(back, 0, cy - ry * 0.3, "tail", 1, 0, 0);
  limbs.push(tail.l);
  drawers.push((c) => {
    const g = tail.g;
    g.clear();
    const n = 6;
    const top = H * (0.45 + 0.35 * tl);
    let lx = 0,
      ly = 0;
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      const x = Math.sin(t * Math.PI * 1.1) * H * 0.26 * (0.7 + 0.3 * tl);
      const y = -Math.sin(t * Math.PI * 0.6) * top;
      const r = H * (0.085 - 0.03 * t);
      disc(g, x, y, r, i % 2 ? c.base : gn.markings.multi ? pal(c, i >> 1) : mix(c.base, c.accent, 0.3), ow * 0.85);
      lx = x;
      ly = y;
    }
    blob(
      g,
      [lx - H * 0.05, ly - H * 0.01, lx - H * 0.17, ly + H * 0.1, lx + H * 0.03, ly + H * 0.05],
      mix(c.accent, INK, 0.2),
      ow * 0.85,
      false,
    );
  });
  for (const s of sides) {
    const legs = limb(back, s * rx * 0.6, cy + ry * 0.4, "leg", s, 0, 0);
    limbs.push(legs.l);
    drawers.push((c) => {
      const g = legs.g;
      g.clear();
      const pairs = gn.legPairs + 1;
      for (let i = 0; i < pairs; i++) {
        const kx = s * (rx * 0.35 + H * 0.12 - i * H * 0.06),
          fx = s * (rx * 0.38 + H * 0.2 - i * H * 0.1);
        noodle(g, [-s * i * rx * 0.15, 0, kx, -H * 0.05, fx, -(cy + ry * 0.4)], H * 0.035, c.accent, ow * 0.75, true);
      }
    });
    snapClaw(front, limbs, drawers, gn, s, s * rx * 0.85, cy + ry * 0.1, H, ow, cs);
  }
  const drawBody = (c: Colors) => {
    const g = body;
    g.clear();
    drawHorns(g, gn, cy - ry * 0.9, rx * 0.3, H, c, ow);
    drawMohawk(g, gn, 0, cy - ry, H, c, ow);
    drawBackSpikes(g, gn, 0, cy, rx, ry, H, c, ow, -0.95, -0.05);
    bodyOval(g, gn, 0, cy, rx, ry, c.base, ow);
    // Armour plates.
    for (const k of [-0.45, 0.45]) inkLine(g, [k * rx, cy - ry * 0.85, k * rx * 1.12, cy + ry * 0.2], ow * 0.6, true);
    blot(g, 0, cy + ry * 0.55, rx * 0.55, ry * 0.3, c.belly);
    drawMarkings(g, gn, cy, rx, ry, H, c);
    drawBlush(g, gn, e.eyeY, e.eyeDx, e.eyeR);
  };
  return { geo, back, body, front, limbs, drawers, drawBody };
}

// ---------------------------------------------------------------------------------------------
// Snapper / baby dragon (fantasy): chubby upright lizard, snout with snapping jaws, little
// snapping claws, bat wings, back spikes, spade tail.

export function dragon(gn: CreatureGenome, H: number, ow: number): Anatomy {
  const legH = H * 0.07 * gn.limbLength,
    ry = H * 0.27 * gn.heightScale,
    rx = ry * gn.bodyAspect * 0.78,
    cy = -legH - ry;
  const hr = H * 0.23,
    hy = cy - ry * 0.85 - hr * 0.55;
  const e = eyeLayout(gn, H, hr * 1.1, hy - hr * 0.2, 0.85);
  const snoutY = hy + hr * 0.45;
  const geo: Geo = {
    cy,
    rx,
    ry,
    centerY: (cy + hy) / 2,
    halfW: rx + H * 0.15,
    headTopY: hy - hr,
    headW: hr * 1.6,
    topY: hy - hr - H * 0.15,
    ...e,
    mouthY: snoutY + H * 0.06,
    mouthW: hr * 1.1 * Math.max(0.6, gn.mouthWidth * 2.5),
    neckY: hy + hr * 0.95,
    neckHalfW: hr * 0.85,
    floats: false,
    fangs: true,
  };
  const { back, front, body, limbs, drawers } = newAnatomyParts();
  // Bat wings (hanging-down frame, lifted by the rest angle; membrane on the inner side).
  for (const s of sides) {
    const w = limb(back, s * rx * 0.55, cy - ry * 0.75, "wing", s, 0, 2.25);
    limbs.push(w.l);
    const L = H * 0.42 * gn.span,
      W = H * 0.3 * gn.span;
    drawers.push((c) => {
      const g = w.g;
      g.clear();
      blob(
        g,
        [0, 0, 0, L, -s * W * 0.5, L * 0.78, -s * W * 0.72, L * 0.5, -s * W * 0.62, L * 0.22, -s * W * 0.35, L * 0.02],
        mix(c.base, c.accent, 0.35),
        ow,
        false,
      );
      noodle(g, [0, 0, 0, L], H * 0.035, c.accent, ow * 0.7);
    });
  }
  const tail = limb(back, rx * 0.55, cy + ry * 0.6, "tail", 1, 0, 0);
  limbs.push(tail.l);
  drawers.push((c) => {
    const g = tail.g;
    g.clear();
    const L = H * 0.3 * gn.tailLength;
    noodle(g, [0, 0, L * 0.6, H * 0.04, L, -L * 0.25], H * 0.07, c.base, ow, true);
    blob(
      g,
      [
        L,
        -L * 0.25 + H * 0.05,
        L - H * 0.04,
        -L * 0.25 - H * 0.04,
        L + H * 0.03,
        -L * 0.25 - H * 0.12,
        L + H * 0.1,
        -L * 0.25 - H * 0.03,
      ],
      pal(c, 0),
      ow * 0.85,
      false,
    );
  });
  for (const s of sides) {
    const leg = limb(back, s * rx * 0.5, -legH * 1.3, "leg", s, 0, 0);
    limbs.push(leg.l);
    drawers.push((c) => {
      leg.g.clear();
      oval(leg.g, s * H * 0.02, legH * 0.8, H * 0.09, H * 0.06, c.base, ow);
    });
    snapClaw(
      front,
      limbs,
      drawers,
      gn,
      s,
      s * rx * 0.95,
      cy + ry * 0.35,
      H * 0.55,
      ow,
      gn.clawSize * (0.8 + 0.25 * gn.span),
    );
  }
  const drawBody = (c: Colors) => {
    const g = body;
    g.clear();
    // Horns always; spiky dragons get longer ones.
    const hc = mix(c.belly, 0xffffff, 0.35),
      hl = H * (0.1 + 0.08 * gn.traits.spikiness);
    for (const s of sides) {
      const bx = s * hr * 0.55,
        by = hy - hr * 0.6;
      blob(
        g,
        [
          bx - H * 0.045,
          by + H * 0.03,
          bx + s * H * 0.02,
          by - hl * 0.6,
          bx + s * H * 0.09,
          by - hl * 1.05,
          bx + s * H * 0.03,
          by - hl * 0.45,
          bx + H * 0.045,
          by + H * 0.03,
        ],
        hc,
        ow,
        false,
      );
    }
    drawMohawk(g, gn, 0, hy - hr, H, c, ow);
    // Back ridge: at least three spikes over head and back.
    const ridge = { ...gn, spikes: Math.max(3, gn.spikes) };
    drawBackSpikes(g, ridge, 0, hy, hr, hr, H, c, ow, -0.62, -0.38);
    // Body + head fused.
    g.ellipse(0, cy, rx, ry)
      .circle(0, hy, hr)
      .stroke({ width: ow * 2, color: INK });
    g.ellipse(0, cy, rx, ry).fill({ color: c.base });
    g.circle(0, hy, hr).fill({ color: c.base });
    // Scaled belly.
    blot(g, 0, cy + ry * 0.15, rx * 0.6, ry * 0.75, c.belly);
    for (let i = 0; i < 3; i++) {
      const y = cy - ry * 0.25 + i * ry * 0.3;
      const hw = chord(rx * 0.6, ry * 0.75, y - cy - ry * 0.15) * 0.85;
      if (hw > 2)
        g.moveTo(-hw, y)
          .quadraticCurveTo(0, y + ry * 0.08, hw, y)
          .stroke({ width: ow * 0.5, color: mix(c.belly, INK, 0.35), cap: "round" });
    }
    drawMarkings(g, gn, cy, rx * 0.95, ry * 0.95, H, c);
    // Snout.
    oval(g, 0, snoutY, hr * 0.7, hr * 0.4, mix(c.base, c.belly, 0.35), ow * 0.8);
    for (const s of sides) blot(g, s * hr * 0.22, snoutY - hr * 0.12, H * 0.016, H * 0.012, INK);
    drawBlush(g, gn, e.eyeY, e.eyeDx, e.eyeR);
  };
  return { geo, back, body, front, limbs, drawers, drawBody };
}

// ---------------------------------------------------------------------------------------------
// Chirper / owl: tall oval, ear tufts, facial disc, small beak, folded wings, talons.

export function owl(gn: CreatureGenome, H: number, ow: number): Anatomy {
  const legH = H * 0.07 * gn.limbLength,
    ry = H * 0.42 * gn.heightScale,
    rx = ry * gn.bodyAspect * 0.86,
    cy = -legH - ry;
  const e = eyeLayout(gn, H, rx, cy - ry * 0.38, 1.05);
  const geo: Geo = {
    cy,
    rx,
    ry,
    centerY: cy,
    halfW: rx + H * 0.05,
    headTopY: cy - ry,
    headW: rx * 1.2,
    topY: cy - ry - H * 0.16,
    ...e,
    mouthY: e.eyeY + e.eyeR * 0.75,
    mouthW: H * 0.09,
    neckY: e.eyeY + e.eyeR * 2.1,
    neckHalfW: chord(rx, ry, e.eyeY + e.eyeR * 2.1 - cy),
    floats: false,
    mouthStyle: "beak",
    beakScale: 0.95 + gn.beakLength * 0.25,
  };
  const { back, front, body, limbs, drawers } = newAnatomyParts();
  const talons = limb(back, 0, 0, "leg", 0, 0, 0);
  limbs.push(talons.l);
  drawers.push(() => {
    const g = talons.g;
    g.clear();
    for (const s of sides)
      for (const k of [-1, 0, 1])
        noodle(g, [s * rx * 0.3, -legH * 1.5, s * rx * 0.3 + k * H * 0.035, 0], H * 0.03, 0xe69f00, ow * 0.6);
  });
  for (const s of sides) {
    const wing = limb(front, s * rx * 0.88, cy - ry * 0.2, "wing", s, 0, 0.12);
    limbs.push(wing.l);
    const L = H * 0.36 * (0.8 + 0.25 * gn.span),
      W = H * 0.1 * gn.span;
    drawers.push((c) => {
      const g = wing.g;
      g.clear();
      blob(
        g,
        [-W, L * 0.1, 0, -L * 0.05, W, L * 0.12, W * 0.6, L * 0.8, 0, L, -W * 0.6, L * 0.8],
        mix(c.base, c.accent, 0.5),
        ow,
        true,
      );
      for (let k = 1; k <= 2; k++)
        inkLine(
          g,
          [-W * 0.55, L * (0.35 + k * 0.18), 0, L * (0.45 + k * 0.18), W * 0.55, L * (0.35 + k * 0.18)],
          ow * 0.5,
          true,
        );
    });
  }
  const drawBody = (c: Colors) => {
    const g = body;
    g.clear();
    // Ear tufts.
    const tuft = H * (0.14 + 0.06 * gn.traits.spikiness);
    for (const s of sides)
      blob(
        g,
        [s * rx * 0.25, cy - ry * 0.85, s * rx * 0.85, cy - ry * 0.9 - tuft, s * rx * 0.75, cy - ry * 0.5],
        gn.markings.multi ? pal(c, 0) : c.accent,
        ow,
        false,
      );
    drawMohawk(g, gn, 0, cy - ry, H, c, ow);
    drawBackSpikes(g, gn, 0, cy, rx, ry, H, c, ow, -0.7, -0.3);
    bodyOval(g, gn, 0, cy, rx, ry, c.base, ow);
    // Chest with feather chevrons.
    blot(g, 0, cy + ry * 0.35, rx * 0.62, ry * 0.52, c.belly);
    for (let r = 0; r < 3; r++)
      for (let k = -1; k <= 1; k++) {
        const x = k * rx * 0.28 + (r % 2) * rx * 0.14 - rx * 0.07,
          y = cy + ry * (0.15 + r * 0.2);
        inkLine(g, [x - H * 0.03, y, x, y + H * 0.025, x + H * 0.03, y], ow * 0.5, false, mix(c.accent, INK, 0.2));
      }
    drawMarkings(g, gn, cy, rx, ry * 0.7, H, c, 0.8);
    // Facial disc.
    for (const s of sides) {
      const x = s * e.eyeDx,
        room = chord(rx, ry, e.eyeY - cy) - Math.abs(x) - ow;
      g.circle(x, e.eyeY, Math.max(e.eyeR * 1.1, Math.min(e.eyeR * 1.45, room, e.eyeY - (cy - ry) - ow))).fill({
        color: mix(c.belly, 0xffffff, 0.35),
      });
    }
    drawBlush(g, gn, e.eyeY + e.eyeR * 0.5, e.eyeDx, e.eyeR);
  };
  return { geo, back, body, front, limbs, drawers, drawBody };
}

// ---------------------------------------------------------------------------------------------
// Chirper / fairy (fantasy): round head, petal dress, two pairs of glassy wings, glowing antennae.

export function fairy(gn: CreatureGenome, H: number, ow: number): Anatomy {
  const hr = H * 0.22,
    hy = -H * (0.6 + 0.12 * (gn.heightScale - 1) * 3) - H * 0.04 * gn.limbLength;
  const dressTop = hy + hr * 0.75,
    dressBot = -H * 0.14 * gn.limbLength,
    dw = H * 0.2 * gn.bodyAspect;
  const e = eyeLayout(gn, H, hr * 1.1, hy + hr * 0.05, 0.85);
  const geo: Geo = {
    cy: hy,
    rx: hr,
    ry: hr,
    centerY: (hy + dressBot) / 2,
    halfW: H * 0.4 * gn.span,
    headTopY: hy - hr,
    headW: hr * 1.7,
    topY: hy - hr - H * 0.25,
    ...e,
    mouthY: hy + hr * 0.5,
    mouthW: gn.mouthWidth * hr * 1.5,
    neckY: dressTop,
    neckHalfW: hr * 0.55,
    floats: true,
    selfGlow: 0.25,
  };
  const { back, front, body, limbs, drawers } = newAnatomyParts();
  for (const s of sides) {
    insectWing(
      back,
      limbs,
      drawers,
      s * hr * 0.35,
      dressTop + H * 0.03,
      H * 0.42 * gn.span,
      H * 0.12 * gn.span,
      2.25,
      s,
      0,
      ow,
      FAIRY_WING,
    );
    insectWing(
      back,
      limbs,
      drawers,
      s * hr * 0.35,
      dressTop + H * 0.07,
      H * 0.28 * gn.span,
      H * 0.09 * gn.span,
      1.35,
      s,
      1,
      ow,
      FAIRY_WING,
    );
  }
  antennae(back, limbs, drawers, gn, hy - hr * 0.8, hr * 0.35, H * 0.2, H, ow, true, 0, 2);
  const legs = limb(back, 0, 0, "leg", 0, 0, 0);
  limbs.push(legs.l);
  drawers.push((c) => {
    const g = legs.g;
    g.clear();
    for (const s of sides) {
      noodle(g, [s * dw * 0.3, dressBot - H * 0.02, s * dw * 0.32, -H * 0.03], H * 0.025, c.belly, ow * 0.6);
      oval(g, s * dw * 0.36, -H * 0.03, H * 0.035, H * 0.022, pal(c, 1, c.accent), ow * 0.6);
    }
  });
  for (const s of sides) {
    const arm = limb(front, s * dw * 0.45, dressTop + H * 0.05, "arm", s, 0, 0.45);
    limbs.push(arm.l);
    drawers.push((c) => {
      arm.g.clear();
      noodle(arm.g, [0, 0, 0, H * 0.12], H * 0.035, c.belly, ow * 0.7);
    });
  }
  const drawBody = (c: Colors) => {
    const g = body;
    g.clear();
    drawMohawk(g, gn, 0, hy - hr, H, c, ow);
    drawTufts(g, gn, 0, hy - hr, hr, H, c.base, ow);
    drawBackSpikes(g, gn, 0, hy, hr, hr, H, c, ow, -0.8, -0.2);
    // Petal dress: scalloped hem, petals alternate palette colours.
    const petals = 4 + (gn.palette.length > 1 ? 1 : 0);
    g.moveTo(-hr * 0.4, dressTop);
    g.lineTo(-dw, dressBot);
    for (let i = 0; i < petals; i++) {
      const x0 = -dw + (i / petals) * dw * 2,
        x1 = -dw + ((i + 1) / petals) * dw * 2;
      g.quadraticCurveTo((x0 + x1) / 2, dressBot + H * 0.07, x1, dressBot);
    }
    g.lineTo(hr * 0.4, dressTop)
      .closePath()
      .fill({ color: mix(c.base, c.accent, 0.25) })
      .stroke({ width: ow, color: INK, join: "round" });
    for (let i = 0; i < petals; i++) {
      const x = -dw + ((i + 0.5) / petals) * dw * 2;
      blot(g, x, dressBot - H * 0.005, (dw / petals) * 0.6, H * 0.04, i % 2 ? pal(c, i >> 1, c.light) : c.light, 0.9);
    }
    bodyOval(g, gn, 0, hy, hr, hr, c.base, ow);
    blot(g, 0, hy + hr * 0.3, hr * 0.7, hr * 0.5, c.belly, 0.8);
    drawMarkings(g, gn, hy, hr, hr, H, c, 0.8);
    drawBlush(g, gn, e.eyeY, e.eyeDx, e.eyeR);
  };
  return { geo, back, body, front, limbs, drawers, drawBody };
}

// ---------------------------------------------------------------------------------------------
// Clapper / penguin: tall egg, white face mask + belly, orange beak + feet, flippers that clap.

export function penguin(gn: CreatureGenome, H: number, ow: number): Anatomy {
  const legH = H * 0.04,
    ry = H * 0.45 * gn.heightScale,
    rx = ry * gn.bodyAspect * 0.78,
    cy = -legH - ry;
  const e = eyeLayout(gn, H, rx, cy - ry * 0.45, 0.85);
  const geo: Geo = {
    cy,
    rx,
    ry,
    centerY: cy,
    halfW: rx + H * 0.1,
    headTopY: cy - ry,
    headW: rx * 1.2,
    topY: cy - ry - H * 0.05,
    ...e,
    mouthY: e.eyeY + e.eyeR * 1.1,
    mouthW: H * 0.1,
    neckY: e.eyeY + e.eyeR * 2.6,
    neckHalfW: chord(rx, ry, e.eyeY + e.eyeR * 2.6 - cy) * 1.02,
    floats: false,
    mouthStyle: "beak",
    beakScale: 1.35,
  };
  const { back, front, body, limbs, drawers } = newAnatomyParts();
  const feet = limb(back, 0, 0, "leg", 0, 0, 0);
  limbs.push(feet.l);
  drawers.push(() => {
    feet.g.clear();
    for (const s of sides) oval(feet.g, s * rx * 0.35, -H * 0.02, H * 0.09, H * 0.04, 0xe69f00, ow * 0.8);
  });
  for (const s of sides) {
    const fl = limb(front, s * rx * 0.88, cy - ry * 0.15, "flipper", s, 0, 0.35);
    limbs.push(fl.l);
    drawers.push((c) => {
      fl.g.clear();
      blob(
        fl.g,
        [-H * 0.05, 0, H * 0.05, 0, H * 0.035, H * 0.25 * gn.span, 0, H * 0.3 * gn.span, -H * 0.04, H * 0.24 * gn.span],
        mix(c.base, INK, 0.25),
        ow,
        true,
      );
    });
  }
  const drawBody = (c: Colors) => {
    const g = body;
    g.clear();
    drawMohawk(g, gn, 0, cy - ry, H, c, ow);
    drawTufts(g, gn, 0, cy - ry, rx * 0.8, H, c.base, ow);
    drawBackSpikes(g, gn, 0, cy, rx, ry, H, c, ow, -0.7, -0.3);
    bodyOval(g, gn, 0, cy, rx, ry, c.base, ow);
    const white = mix(c.belly, 0xffffff, 0.6);
    blot(g, 0, cy + ry * 0.25, rx * 0.68, ry * 0.68, white);
    for (const s of sides) g.circle(s * e.eyeDx * 0.95, e.eyeY + e.eyeR * 0.2, e.eyeR * 1.55).fill({ color: white });
    drawMarkings(g, gn, cy + ry * 0.35, rx * 0.8, ry * 0.5, H, c, 0.9);
    // Rockhopper brows for colourful penguins.
    if (gn.palette.length >= 2)
      for (const s of sides)
        noodle(
          g,
          [s * (e.eyeDx + e.eyeR * 0.4), e.eyeY - e.eyeR * 1.1, s * (e.eyeDx + e.eyeR * 1.4), e.eyeY - e.eyeR * 1.9],
          H * 0.03,
          pal(c, 1),
          ow * 0.6,
        );
    drawBlush(g, gn, e.eyeY, e.eyeDx, e.eyeR);
  };
  return { geo, back, body, front, limbs, drawers, drawBody };
}

// ---------------------------------------------------------------------------------------------
// Clapper / yeti (fantasy): big shaggy body, skin face, little horns, long furry arms that clap.

export function yeti(gn: CreatureGenome, H: number, ow: number): Anatomy {
  const legH = H * 0.07,
    ry = H * 0.43 * gn.heightScale,
    rx = ry * gn.bodyAspect * 1.08,
    cy = -legH - ry;
  const fy = cy - ry * 0.3,
    fr = Math.min(rx, ry) * 0.5;
  const e = eyeLayout(gn, H, fr * 1.4, fy - fr * 0.2, 0.8);
  const geo: Geo = {
    cy,
    rx,
    ry,
    centerY: cy,
    halfW: rx + H * 0.12,
    headTopY: cy - ry,
    headW: rx * 1.1,
    topY: cy - ry - H * 0.12,
    ...e,
    mouthY: fy + fr * 0.5,
    mouthW: fr * 1.2 * Math.max(0.6, gn.mouthWidth * 2.5),
    neckY: fy + fr * 1.15,
    neckHalfW: chord(rx, ry, fy + fr * 1.15 - cy),
    floats: false,
  };
  const { back, front, body, limbs, drawers } = newAnatomyParts();
  const fur = (c: Colors) => mix(c.base, 0xffffff, 0.5);
  const fz = Math.max(0.6, gn.fuzz);
  const feet = limb(back, 0, 0, "leg", 0, 0, 0);
  limbs.push(feet.l);
  drawers.push((c) => {
    feet.g.clear();
    for (const s of sides) oval(feet.g, s * rx * 0.4, -H * 0.04, H * 0.12, H * 0.055, mix(c.accent, 0xffffff, 0.2), ow);
  });
  for (const s of sides) {
    const arm = limb(front, s * rx * 0.8, cy - ry * 0.25, "flipper", s, 0, 0.35);
    limbs.push(arm.l);
    const L = H * 0.34 * gn.span;
    drawers.push((c) => {
      const g = arm.g;
      g.clear();
      traceFur(g, 0, L * 0.45, H * 0.075, L * 0.52, fz)
        .fill({ color: fur(c) })
        .stroke({ width: ow, color: INK, join: "round" });
      oval(g, 0, L * 0.95, H * 0.06, H * 0.05, mix(c.accent, 0xffffff, 0.2), ow * 0.8);
    });
  }
  const drawBody = (c: Colors) => {
    const g = body;
    g.clear();
    const hc = mix(c.belly, 0xfff3a8, 0.4);
    for (const s of sides)
      noodle(
        g,
        [s * rx * 0.35, cy - ry * 0.75, s * rx * 0.5, cy - ry * 1.05, s * rx * 0.68, cy - ry * 1.08],
        H * 0.05,
        hc,
        ow,
        true,
      );
    drawMohawk(g, gn, 0, cy - ry, H, c, ow);
    drawBackSpikes(g, gn, 0, cy, rx, ry, H, c, ow);
    traceFur(g, 0, cy, rx, ry, fz)
      .fill({ color: fur(c) })
      .stroke({ width: ow, color: INK, join: "round" });
    // Skin face + belly patch.
    oval(g, 0, fy, fr * 1.35, fr * 1.05, mix(c.base, c.accent, 0.3), ow * 0.7);
    blot(g, 0, cy + ry * 0.45, rx * 0.5, ry * 0.32, mix(fur(c), c.base, 0.25));
    drawMarkings(g, gn, cy, rx, ry, H, gn.markings.multi ? c : mix(c.base, 0xffffff, 0.2), 0.8);
    drawBlush(g, gn, e.eyeY, e.eyeDx, e.eyeR);
  };
  return { geo, back, body, front, limbs, drawers, drawBody };
}

// ---------------------------------------------------------------------------------------------
// Wobbler / octopus: rippling mantle, curling tentacles with suckers.

const OCT_N = 32;

export function octopus(gn: CreatureGenome, H: number, ow: number): Anatomy {
  const ry = H * 0.32 * gn.heightScale,
    rx = H * 0.34 * gn.bodyAspect * 0.85,
    cy = -H * 0.3 - ry * 0.75;
  const e = eyeLayout(gn, H, rx, cy + ry * 0.12, 1.0);
  const geo: Geo = {
    cy,
    rx,
    ry,
    centerY: cy,
    halfW: rx * 1.3,
    headTopY: cy - ry,
    headW: rx * 1.1,
    topY: cy - ry - H * 0.08,
    ...e,
    mouthY: e.eyeY + e.eyeR + H * 0.07,
    mouthW: gn.mouthWidth * rx * 0.9,
    neckY: cy + ry * 0.8,
    neckHalfW: rx * 0.85,
    floats: false,
  };
  const { back, front, body, limbs, drawers } = newAnatomyParts();
  const n = 6;
  const baseY = cy + ry * 0.7;
  for (let i = 0; i < n; i++) {
    const t = (i / (n - 1)) * 2 - 1;
    const td = limb(back, t * rx * 0.7, baseY, "tendril", 0, i, 0);
    limbs.push(td.l);
    const down = -baseY - H * 0.04;
    const out = Math.sign(t || 1) * (H * 0.06 + Math.abs(t) * H * 0.12) * (0.7 + 0.4 * gn.tailLength);
    const curl = H * 0.07 * gn.tailLength;
    drawers.push((c) => {
      const g = td.g;
      g.clear();
      const pts = [
        0,
        0,
        out * 0.3,
        down * 0.55,
        out,
        down,
        out + Math.sign(out) * curl,
        down - curl * 0.4,
        out + Math.sign(out) * curl * 0.6,
        down - curl * 1.1,
      ];
      noodle(
        g,
        pts,
        H * 0.07 * (1 - Math.abs(t) * 0.15),
        i % 2 && gn.markings.multi ? pal(c, i >> 1) : c.base,
        ow,
        true,
      );
      for (let k = 1; k <= 2; k++)
        blot(g, out * (0.3 + k * 0.25), down * (0.45 + k * 0.2), H * 0.014, H * 0.014, c.belly);
    });
  }
  const pts = new Array<number>(OCT_N * 2).fill(0);
  const drawBody = (c: Colors, ripple: number, phase: number) => {
    const g = body;
    g.clear();
    drawHorns(g, gn, cy - ry * 0.9, rx * 0.35, H, c, ow);
    drawMohawk(g, gn, 0, cy - ry, H, c, ow);
    drawBackSpikes(g, gn, 0, cy, rx, ry, H, c, ow);
    for (let i = 0; i < OCT_N; i++) {
      const a = (i / OCT_N) * Math.PI * 2;
      const fur = gn.fuzz >= 0.25 && Math.sin(a) < 0.2 ? gn.fuzz * 0.05 * (i % 2 ? 1 : -0.4) : 0;
      const rip = 1 + fur + ripple * 0.06 * Math.sin(a * 5 + phase);
      // Slightly bulbous top.
      const k = Math.sin(a) < 0 ? 1.08 : 0.92;
      pts[i * 2] = Math.cos(a) * rx * rip;
      pts[i * 2 + 1] = cy + Math.sin(a) * ry * k * rip;
    }
    blob(g, pts, c.base, ow, true);
    drawMarkings(g, gn, cy - ry * 0.2, rx * 0.9, ry * 0.7, H, c, 0.85);
    blot(g, -rx * 0.4, cy - ry * 0.55, rx * 0.15, ry * 0.1, 0xffffff, 0.55);
    drawBlush(g, gn, e.eyeY, e.eyeDx, e.eyeR);
  };
  return { geo, back, body, front, limbs, drawers, drawBody };
}

// ---------------------------------------------------------------------------------------------
// Wobbler / ghost (fantasy): floating sheet with a wavy hem that ripples, stubby arms.

const GHOST_N = 24;

export function ghost(gn: CreatureGenome, H: number, ow: number): Anatomy {
  const rx = H * 0.33 * gn.bodyAspect * 0.85,
    top = -H * 0.95 * gn.heightScale,
    hem = -H * 0.14;
  const dr = Math.min(rx, (hem - top) * 0.5); // dome vertical radius
  const domeY = top + dr; // centre of the dome
  const e = eyeLayout(gn, H, rx, domeY + dr * 0.15);
  const geo: Geo = {
    cy: (domeY + hem) / 2,
    rx,
    ry: (hem - top) / 2,
    centerY: (top + hem) / 2,
    halfW: rx * 1.25,
    headTopY: top,
    headW: rx * 1.2,
    topY: top - H * 0.08,
    ...e,
    mouthY: e.eyeY + e.eyeR + H * 0.08,
    mouthW: gn.mouthWidth * rx * 1.2,
    neckY: domeY + dr * 0.75,
    neckHalfW: rx * 1.02,
    floats: true,
    selfGlow: 0.15,
  };
  const { back, front, body, limbs, drawers } = newAnatomyParts();
  for (const s of sides) {
    const pod = limb(front, s * rx * 0.98, domeY + (hem - domeY) * 0.45, "pod", s, 0, 0.9);
    limbs.push(pod.l);
    drawers.push((c) => {
      pod.g.clear();
      oval(pod.g, 0, H * 0.07 * gn.span, H * 0.05, H * 0.09 * gn.span, mix(c.base, 0xffffff, 0.35), ow);
    });
  }
  const pts: number[] = [];
  const drawBody = (c: Colors, ripple: number, phase: number) => {
    const g = body;
    g.clear();
    drawHorns(g, gn, top + H * 0.05, rx * 0.4, H, c, ow);
    drawMohawk(g, gn, 0, top, H, c, ow);
    drawTufts(g, gn, 0, top, rx * 0.8, H, mix(c.base, 0xffffff, 0.35), ow);
    drawBackSpikes(g, gn, 0, domeY, rx, dr, H, c, ow);
    pts.length = 0;
    // Dome from left to right over the top.
    for (let i = 0; i <= 12; i++) {
      const a = Math.PI + (i / 12) * Math.PI;
      pts.push(Math.cos(a) * rx, domeY + Math.sin(a) * dr);
    }
    // Right side down, flaring a little.
    pts.push(rx * 1.08, (domeY + hem) / 2, rx * 1.15, hem);
    // Wavy hem right → left; ripple makes the waves travel.
    const waves = 4;
    for (let i = 1; i < GHOST_N; i++) {
      const t = i / GHOST_N;
      const x = rx * 1.15 - t * rx * 2.3;
      const w = Math.sin(t * Math.PI * 2 * waves + phase * (ripple > 0 ? 1 : 0)) * H * (0.035 + ripple * 0.025);
      pts.push(x, hem + w);
    }
    pts.push(-rx * 1.15, hem, -rx * 1.08, (domeY + hem) / 2);
    blob(g, pts, mix(c.base, 0xffffff, 0.35), ow, true);
    blot(g, -rx * 0.45, domeY - dr * 0.5, rx * 0.16, dr * 0.1, 0xffffff, 0.7);
    drawMarkings(g, gn, domeY + dr * 0.4, rx * 0.9, dr * 0.9, H, c, 0.6);
    drawBlush(g, gn, e.eyeY, e.eyeDx, e.eyeR);
  };
  return { geo, back, body, front, limbs, drawers, drawBody };
}

// ---------------------------------------------------------------------------------------------
// Glimmer / firefly: head + glowing lantern abdomen, glassy wings, antennae.

export function firefly(gn: CreatureGenome, H: number, ow: number): Anatomy {
  const hr = H * 0.2,
    hy = -H * 0.66 * gn.heightScale;
  const ary = H * 0.19 * gn.heightScale,
    arx = ary * gn.bodyAspect * 0.95,
    ay = hy + hr * 0.75 + ary * 0.9;
  const e = eyeLayout(gn, H, hr * 1.15, hy, 0.95);
  const geo: Geo = {
    cy: ay,
    rx: arx,
    ry: ary,
    centerY: (hy + ay) / 2,
    halfW: H * 0.38 * gn.span,
    headTopY: hy - hr,
    headW: hr * 1.7,
    topY: hy - hr - H * 0.28,
    ...e,
    mouthY: hy + hr * 0.55,
    mouthW: gn.mouthWidth * hr * 1.6,
    neckY: hy + hr * 0.95,
    neckHalfW: hr * 0.7,
    floats: true,
  };
  const { back, front, body, limbs, drawers } = newAnatomyParts();
  for (const s of sides)
    insectWing(back, limbs, drawers, s * hr * 0.4, hy + hr * 0.9, H * 0.34 * gn.span, H * 0.1 * gn.span, 2.0, s, 0, ow);
  antennae(back, limbs, drawers, gn, hy - hr * 0.8, hr * 0.4, H * 0.24, H, ow, true, 0, Math.max(2, gn.antennae));
  const legs = limb(back, 0, 0, "leg", 0, 0, 0);
  limbs.push(legs.l);
  drawers.push((c) => {
    legs.g.clear();
    for (const s of sides)
      for (let k = 0; k < 3; k++)
        noodle(
          legs.g,
          [s * arx * 0.5, ay - ary * 0.5 + k * ary * 0.3, s * (arx + H * 0.06), ay - ary * 0.2 + k * ary * 0.35],
          H * 0.025,
          c.accent,
          ow * 0.6,
        );
  });
  const drawBody = (c: Colors) => {
    const g = body;
    g.clear();
    drawMohawk(g, gn, 0, hy - hr, H, c, ow);
    drawTufts(g, gn, 0, hy - hr, hr, H, c.base, ow);
    drawBackSpikes(g, gn, 0, hy, hr, hr, H, c, ow, -0.8, -0.2);
    // Lantern abdomen with segment bands.
    const lamp = mix(c.light, 0xfff3a8, 0.65);
    bodyOval(g, gn, 0, ay, arx, ary, lamp, ow);
    for (let i = 0; i < 2; i++) {
      const y = ay - ary * 0.5 + i * ary * 0.32;
      const hw = chord(arx, ary, y - ay) * 0.95;
      g.moveTo(-hw, y)
        .quadraticCurveTo(0, y + ary * 0.15, hw, y)
        .stroke({ width: ow * 0.9, color: gn.markings.multi ? pal(c, i) : c.accent, cap: "round" });
    }
    blot(g, 0, ay + ary * 0.25, arx * 0.55, ary * 0.4, 0xffffff, 0.5);
    // Head.
    bodyOval(g, gn, 0, hy, hr * 1.05, hr, c.base, ow);
    blot(g, 0, hy + hr * 0.4, hr * 0.6, hr * 0.35, c.belly, 0.8);
    drawMarkings(g, gn, hy, hr, hr, H, c, 0.8);
    drawBlush(g, gn, e.eyeY, e.eyeDx, e.eyeR);
  };
  return { geo, back, body, front, limbs, drawers, drawBody };
}

// ---------------------------------------------------------------------------------------------
// Glimmer / wisp (fantasy, phoenix-like): flame body with flickering tongues, plume tail, feather wings.

export function wisp(gn: CreatureGenome, H: number, ow: number): Anatomy {
  const r = H * 0.25 * (0.9 + 0.1 * gn.bodyAspect),
    cy = -H * 0.42,
    tipY = cy - r - H * 0.42 * gn.heightScale;
  const e = eyeLayout(gn, H, r, cy - r * 0.05, 0.95);
  const geo: Geo = {
    cy,
    rx: r,
    ry: r,
    centerY: cy - r * 0.4,
    halfW: r + H * 0.15 * gn.span,
    headTopY: cy - r * 1.2,
    headW: r * 1.5,
    topY: tipY,
    ...e,
    mouthY: e.eyeY + e.eyeR + H * 0.06,
    mouthW: gn.mouthWidth * r * 1.4,
    neckY: cy + r * 0.6,
    neckHalfW: r * 0.82,
    floats: true,
    selfGlow: 0.2,
  };
  const { back, front, body, limbs, drawers } = newAnatomyParts();
  // Plume tail feathers hanging below (sway like tendrils).
  const plumes = 3 + Math.round(gn.traits.tail * 2);
  for (let i = 0; i < plumes; i++) {
    const t = plumes === 1 ? 0 : (i / (plumes - 1)) * 2 - 1;
    const td = limb(back, t * r * 0.5, cy + r * 0.7, "tendril", 0, i, 0);
    limbs.push(td.l);
    const L = H * 0.28 * gn.tailLength;
    drawers.push((c) => {
      const g = td.g;
      g.clear();
      noodle(g, [0, 0, t * H * 0.06, L * 0.5, t * H * 0.12, L], H * 0.035, pal(c, i, c.accent), ow * 0.7, true);
      oval(g, t * H * 0.12, L, H * 0.035, H * 0.05, i % 2 ? c.light : pal(c, i + 1, c.base), ow * 0.6);
    });
  }
  for (const s of sides) {
    const w = limb(back, s * r * 0.8, cy - r * 0.1, "wing", s, 0, 1.9);
    limbs.push(w.l);
    drawers.push((c) => {
      const g = w.g;
      g.clear();
      for (let k = 0; k < 3; k++)
        noodle(
          g,
          [0, 0, -s * H * 0.04 * k, H * (0.2 - k * 0.04) * gn.span],
          H * 0.06,
          k === 1 ? pal(c, 0, c.light) : c.base,
          ow * 0.8,
          true,
        );
    });
  }
  // Flickering flame tongues: own Graphics, scaled per frame from the base.
  const flame = new Graphics();
  flame.position.set(0, cy - r * 0.3);
  back.addChild(flame);
  drawers.push((c) => {
    flame.clear();
    const top = tipY - (cy - r * 0.3);
    const tongues: [number, number, number][] = [
      [-r * 0.55, top * 0.62, -0.9],
      [0, top, 0],
      [r * 0.55, top * 0.7, 0.9],
    ];
    tongues.forEach(([x, y, lean], i) => {
      blob(
        flame,
        [x - r * 0.45, 0, x + lean * r * 0.2, y * 0.55, x + lean * r * 0.15, y, x + r * 0.45, 0],
        i === 1 ? c.base : pal(c, i, mix(c.base, c.light, 0.4)),
        ow,
        true,
      );
    });
    blob(flame, [-r * 0.3, 0, 0, top * 0.6, r * 0.3, 0], c.light, ow * 0.6, true);
  });
  const drawBody = (c: Colors) => {
    const g = body;
    g.clear();
    drawMohawk(g, gn, 0, cy - r, H, c, ow);
    drawBackSpikes(g, gn, 0, cy, r, r, H, c, ow);
    bodyOval(g, gn, 0, cy, r, r, c.base, ow);
    blot(g, 0, cy + r * 0.15, r * 0.7, r * 0.65, c.light, 0.55);
    drawMarkings(g, gn, cy, r, r, H, gn.markings.multi ? c : mix(c.light, 0xfff3a8, 0.5), 0.8);
    drawBlush(g, gn, e.eyeY, e.eyeDx, e.eyeR);
  };
  const phase = ((gn.seed % 997) / 997) * 6.28;
  const tick: Anatomy["tick"] = (p, t, hitAmt) => {
    const hold = p.state === "hold" ? 1 : 0;
    const f =
      1 +
      0.05 * Math.sin(t * 5.5 + phase) +
      0.03 * Math.sin(t * 3.1) +
      hitAmt * 0.25 +
      hold * 0.12 +
      (p.state === "sleep" ? -0.25 : 0);
    flame.scale.set(1 - (f - 1) * 0.3, f);
  };
  return { geo, back, body, front, limbs, drawers, drawBody, tick };
}

// ---------------------------------------------------------------------------------------------
// Critter / snail: slug foot, spiral shell (palette bands), eyes on stalks.

export function snail(gn: CreatureGenome, H: number, ow: number): Anatomy {
  const hr = H * 0.19,
    hy = -H * 0.24;
  const R = H * 0.3 * gn.heightScale,
    sx = H * 0.2,
    sy = -H * 0.14 - R * 0.95;
  const stalk = H * 0.2 * gn.limbLength;
  const e = eyeLayout(gn, H, hr * 1.2, hy - hr - stalk, 0.85);
  const geo: Geo = {
    cy: hy,
    rx: hr,
    ry: hr,
    centerY: (hy + sy) / 2,
    halfW: H * 0.45 * gn.bodyAspect,
    headTopY: e.eyeY - e.eyeR,
    headW: hr * 1.6,
    topY: Math.min(e.eyeY - e.eyeR, sy - R) - H * 0.04,
    ...e,
    mouthY: hy + hr * 0.4,
    mouthW: gn.mouthWidth * hr * 1.8,
    neckY: hy + hr * 0.95,
    neckHalfW: hr * 0.9,
    floats: false,
  };
  const { back, front, body, limbs, drawers } = newAnatomyParts();
  antennae(back, limbs, drawers, gn, hy - hr * 0.2, hr * 0.5, H * 0.1, H, ow, false, 0, 2);
  const shell = new Graphics();
  back.addChild(shell);
  drawers.push((c) => {
    const g = shell;
    g.clear();
    // Slug foot trailing right behind the shell.
    const footL = H * (0.35 + 0.2 * gn.tailLength) * gn.bodyAspect;
    blob(
      g,
      [-H * 0.25, 0, -H * 0.2, -H * 0.12, footL * 0.6, -H * 0.13, footL, -H * 0.03, footL * 0.9, 0],
      c.belly,
      ow,
      true,
    );
    drawBackSpikes(g, gn, sx, sy, R, R, H, c, ow, -0.85, -0.15);
    disc(g, sx, sy, R, c.base, ow);
    // Spiral bands (palette colours).
    const bands = Math.max(2, 1 + gn.palette.length);
    for (let i = 1; i < bands; i++)
      g.circle(sx + R * 0.06 * i, sy + R * 0.04 * i, R * (1 - i / bands) * 0.95).fill({
        color: pal(c, i - 1, mix(c.base, c.accent, 0.4 * (i % 2))),
      });
    const sp: number[] = [];
    for (let i = 0; i <= 40; i++) {
      const a = i * 0.32,
        rr = R * 0.92 * (1 - i / 44);
      sp.push(sx + Math.cos(a) * rr, sy + Math.sin(a) * rr);
    }
    inkLine(g, sp, ow * 0.8, true);
    if (gn.markings.kind === "spots")
      for (const s of gn.markings.spots.slice(0, 4))
        blot(g, sx + s.x * R * 0.6, sy + s.y * R * 0.6, H * 0.025, H * 0.025, 0xffffff, 0.5);
  });
  const drawBody = (c: Colors) => {
    const g = body;
    g.clear();
    const xs = gn.eyeCount === 1 ? [0] : [-e.eyeDx, e.eyeDx];
    for (const x of xs) noodle(g, [x * 0.6, hy - hr * 0.6, x, e.eyeY], H * 0.045, c.belly, ow);
    drawMohawk(g, gn, 0, hy - hr, H, c, ow);
    bodyOval(g, gn, 0, hy, hr * 1.05, hr, c.belly, ow);
    blot(g, 0, hy + hr * 0.45, hr * 0.6, hr * 0.3, mix(c.belly, 0xffffff, 0.4));
    drawBlush(g, gn, hy, hr * 0.55, hr * 0.4);
  };
  return { geo, back, body, front, limbs, drawers, drawBody };
}

// ---------------------------------------------------------------------------------------------
// Critter / mushroom sprite (fantasy): spotted toadstool cap, stem body with a face, stubby limbs.

export function mushroom(gn: CreatureGenome, H: number, ow: number): Anatomy {
  const legH = H * 0.06 * gn.limbLength;
  const capY = -H * 0.5 * gn.heightScale - legH,
    capRx = H * 0.4 * gn.bodyAspect * 0.9,
    capRy = H * 0.34 * gn.heightScale;
  const sw = H * 0.17 * (0.85 + 0.2 * gn.bodyAspect),
    stemTop = capY - H * 0.02,
    stemBot = -legH;
  const e = eyeLayout(gn, H, sw, capY + H * 0.17, 0.8);
  const geo: Geo = {
    cy: (stemTop + stemBot) / 2,
    rx: sw,
    ry: (stemBot - stemTop) / 2,
    centerY: capY,
    halfW: capRx,
    headTopY: capY - capRy,
    headW: capRx * 1.4,
    topY: capY - capRy - H * 0.06,
    ...e,
    mouthY: e.eyeY + e.eyeR + H * 0.06,
    mouthW: gn.mouthWidth * sw * 1.6,
    neckY: e.eyeY + e.eyeR + H * 0.14,
    neckHalfW: sw * 1.02,
    floats: false,
  };
  const { back, front, body, limbs, drawers } = newAnatomyParts();
  for (const s of sides) {
    const leg = limb(back, s * sw * 0.5, -legH * 1.4, "leg", s, 0, 0);
    limbs.push(leg.l);
    drawers.push((c) => {
      leg.g.clear();
      oval(leg.g, s * H * 0.02, legH * 1.1, H * 0.07, H * 0.045, mix(c.belly, c.accent, 0.3), ow);
    });
    const arm = limb(back, s * sw * 0.9, capY + H * 0.3, "arm", s, 0, 0.5);
    limbs.push(arm.l);
    drawers.push((c) => {
      arm.g.clear();
      oval(arm.g, 0, H * 0.06 * gn.span, H * 0.04, H * 0.075 * gn.span, mix(c.belly, 0xffffff, 0.5), ow);
    });
  }
  const capSpots: { x: number; y: number; r: number }[] = [];
  const rng = new Rng(gn.seed ^ 0x5b0e);
  const ns = 3 + gn.markings.spots.length;
  for (let i = 0; i < ns; i++) {
    const a = Math.PI * (1.12 + (0.76 * (i + rng.range(0.1, 0.9))) / ns);
    const d = rng.range(0.4, 0.78);
    capSpots.push({ x: Math.cos(a) * capRx * d, y: capY + Math.sin(a) * capRy * d, r: H * rng.range(0.035, 0.06) });
  }
  const drawBody = (c: Colors) => {
    const g = body;
    g.clear();
    const stem = mix(c.belly, 0xffffff, 0.55);
    // Stem (behind the cap rim).
    g.roundRect(-sw, stemTop, sw * 2, stemBot - stemTop, sw * 0.7)
      .fill({ color: stem })
      .stroke({ width: ow, color: INK });
    drawMohawk(g, gn, 0, capY - capRy, H, c, ow);
    drawTufts(g, gn, 0, capY - capRy, capRx * 0.6, H, c.base, ow);
    drawBackSpikes(g, gn, 0, capY, capRx, capRy, H, c, ow);
    // Cap: dome + gills rim.
    oval(g, 0, capY, capRx * 0.92, H * 0.06, mix(stem, c.accent, 0.25), ow * 0.8);
    traceDome(g, 0, capY, capRx, capRy).fill({ color: c.base }).stroke({ width: ow, color: INK, join: "round" });
    capSpots.forEach((s, i) => {
      if (Math.abs(s.x) + s.r < capRx * 0.95)
        g.circle(s.x, s.y, s.r).fill({ color: gn.markings.multi ? pal(c, i) : 0xfaf7ee });
    });
    blot(g, -capRx * 0.5, capY - capRy * 0.6, capRx * 0.12, capRy * 0.08, 0xffffff, 0.5);
    drawBlush(g, gn, e.eyeY, e.eyeDx, e.eyeR);
  };
  return { geo, back, body, front, limbs, drawers, drawBody };
}
